import React, { useState, useRef, useEffect } from 'react';
import PropTypes, { checkPropTypes } from 'prop-types';
import { useTranslation } from 'react-i18next';
import { CustomScroll } from '@sumtotal/react-foundation';
import ControlPanelSlider from './ControlPanelSlider';
import ControlPanelContainer from './ControlPanelContainer';
import {
  addIframeClickListeners,
  removeIframeClickListeners
} from '../../../modules/core/services/coreIFrameHelper';
import {
  SideBar,
  ScrollContainer,
  StyledRouteLink,
  DisabledLink,
  StyledLinkContainer,
  CollapsedButton
} from './controlPanelStyles';
import useUserContext from '../../hooks/useUserContext';
import { GetProcessedNodeUrl } from '../../../utils/urlHelper';
/**
 *  Component for Expanded Control Panel Container.
 *
 */
function ExpandedControlPanelContainer(props) {
  const {
    controlPanelItems = defaultProps.controlPanelItems,
    highlightControlPanelItem = defaultProps.highlightControlPanelItem,
    setHighlightCPItem = defaultProps.setHighlightCPItem
  } = props;
  checkPropTypes(
    ExpandedControlPanelContainer.propTypes,
    props,
    'prop',
    ExpandedControlPanelContainer.name
  );
  const { t } = useTranslation(['TRANSLATIONS_RCORE_TOPNAV']);
  const [panelExpanded, setPanelExpanded] = useState(false);
  const [expandButtonClick, _setExpandButtonClick] = useState(false);
  const expandButtonClickRef = useRef(expandButtonClick);
  const [ControlPanelFooter, setControlPanelFooter] = useState({});
  const footerNode = controlPanelItems.Footer;
  const { user } = useUserContext();
  const disableControlPanel = user.isEmulatedUser ? false : user.mustAnswerSecurityQuestions;
  const Link = disableControlPanel ? DisabledLink : StyledRouteLink;
  useEffect(() => {
    if (footerNode != null) {
      footerNode.url = GetProcessedNodeUrl(footerNode);
    }
    setControlPanelFooter(footerNode);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const setExpandButtonClick = (data) => {
    _setExpandButtonClick(data);
    expandButtonClickRef.current = data;
  };

  const clickOfExpandMenu = () => {
    addIframeClickListeners(closeMenu);
    setExpandButtonClick(true);
    setPanelExpanded(true);
  };

  const closeMenu = () => {
    removeIframeClickListeners();
    setExpandButtonClick(true);
    setPanelExpanded(false);
  };

  return (
    <SideBar className="side-bar">
      <ControlPanelSlider
        openControlMenu={panelExpanded}
        closeCallback={closeMenu}
        disabled={disableControlPanel}
      >
        {panelExpanded && (
          <ScrollContainer>
            <CustomScroll>
              <CollapsedButton
                key="collapse_menu"
                iconType="IcomoonIcon"
                iconName="Menu-Collapsed"
                iconSize={1.75}
                btnSize="large"
                name={t('RCORE_CONTROL_PANEL_COLLAPSE_MENU')}
                role="button"
                onItemClick={closeMenu}
                ariaLabel={t('RCORE_CONTROL_PANEL_COLLAPSE_BUTTON')}
                nodeKey="collapse_menu"
                panelExpanded={panelExpanded}
                enableTooltip={panelExpanded}
              />
              <ControlPanelContainer
                controlPanelItems={controlPanelItems}
                highlightControlPanelItem={highlightControlPanelItem}
                setHighlightCPItem={setHighlightCPItem}
                controlpanelId=""
                panelExpanded={panelExpanded}
                panelWidth={panelExpanded ? 17.5 : 5.625}
                closeCallback={closeMenu}
                enableControlPanelItemTooltip
                focusable
              />
            </CustomScroll>
          </ScrollContainer>
        )}

        {!panelExpanded && (
          <>
            <CollapsedButton
              key="expand_menu"
              iconType="IcomoonIcon"
              iconName="Menu-Expanded"
              iconSize={1.75}
              btnSize="large"
              name={t('RCORE_CONTROL_PANEL_EXPAND_MENU')}
              role="button"
              onItemClick={clickOfExpandMenu}
              ariaLabel={t('RCORE_CONTROL_PANEL_EXPAND_BUTTON')}
              nodeKey="expand_menu"
              panelExpanded={panelExpanded}
              disableHoverListener={false}
            />
            <ControlPanelContainer
              controlPanelItems={controlPanelItems}
              highlightControlPanelItem={highlightControlPanelItem}
              setHighlightCPItem={setHighlightCPItem}
              controlpanelId="modal-sidebar-controlpanel-items"
              panelExpanded={panelExpanded}
              panelWidth={panelExpanded ? 17.5 : 5.625}
              closeCallback={closeMenu}
              enableControlPanelItemTooltip
              focusable
            />
          </>
        )}
        {ControlPanelFooter !== null && (
          <StyledLinkContainer panelExpanded={panelExpanded}>
            <Link to={ControlPanelFooter.url}>{ControlPanelFooter.name}</Link>
          </StyledLinkContainer>
        )}
      </ControlPanelSlider>
    </SideBar>
  );
}

// PropType check
ExpandedControlPanelContainer.propTypes = {
  /**
   * Control panel items
   */
  controlPanelItems: PropTypes.shape({
    Nodes: PropTypes.arrayOf(PropTypes.shape({})),
    Footer: PropTypes.shape({ url: PropTypes.string })
  }),
  /**
   * Highlight Control panel item
   */
  highlightControlPanelItem: PropTypes.string,
  /**
   * function highlight Control panel item
   */
  setHighlightCPItem: PropTypes.func
};

const defaultProps = {
  controlPanelItems: {},
  highlightControlPanelItem: '',
  setHighlightCPItem: undefined
};

export default ExpandedControlPanelContainer;
